import { dealerLabel } from "@/lib/naming";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { NavigationFailure, reasonFromError } from "@/components/NavigationFailure";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2, FlaskConical, Mic, MessageSquareText, UserRound, FileText } from "lucide-react";
import { SampleRequestDialog } from "@/components/SampleRequestDialog";
import { PROJECT_STAGE, PROJECT_LOST_REASONS } from "@/lib/statusLabels";
import { useDealerContext } from "@/lib/useDealerContext";
import { Textarea } from "@/components/ui/textarea";

const EVENT_TEXT = (a) => {
  const m = a.meta || {};
  const who = a.actor_name || "Someone";
  switch (a.type) {
    case "project_created": return `${who} created the project`;
    case "owner_changed": return `${who} changed the primary owner${m.from_name ? ` from ${m.from_name}` : ""}${m.to_name ? ` to ${m.to_name}` : ""}${m.reason === "employee_deactivated" ? " (departure continuity)" : ""}`;
    case "owner_unassigned": return `${who} left the project unassigned${m.reason === "employee_deactivated" ? ` — ${m.departed_name || "the owner"} was deactivated` : ""}`;
    case "stage_changed": return `${who} moved the stage from ${PROJECT_STAGE[m.from] || m.from} to ${PROJECT_STAGE[m.to] || m.to}${m.automatic ? " (automatic)" : ""}`;
    case "next_step_changed": return `${who} updated the next step${m.to ? `: "${m.to}"` : " (cleared)"}`;
    case "follow_up_changed": return `${who} ${m.to ? `set the follow-up date to ${m.to}` : "cleared the follow-up date"}`;
    case "product_added": return `${who} added ${m.variant_label || "a product"}`;
    case "sample_requested": return `${who} requested a sample`;
    case "reservation_created": return `${who} created a reservation${m.boxes ? ` — ${m.boxes} boxes` : ""}`;
    case "order_created": return `${who} created order ${m.order_number || ""}`.trim();
    case "quote_status_changed": return `${who} marked the quote ${String(m.status || "").toLowerCase()}`;
    case "proposal_created": return `${who} generated proposal ${m.proposal_number || ""}`.trim();
    case "proposal_accepted": return `${who} marked proposal ${m.proposal_number || ""} accepted`.trim();
    case "proposal_declined": return `${who} marked proposal ${m.proposal_number || ""} declined`.trim();
    default: return a.type;
  }
};

const MILESTONE_LABEL = {
  sample_status: "Sample",
  quote_status: "Quote",
  reservation_status: "Reservation",
  order_status: "Order",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [navFail, setNavFail] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [lineForm, setLineForm] = useState({ variant_id: "", waste_pct: "", area: "", area_sqft: "" });
  const [sampleOpen, setSampleOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retailEdit, setRetailEdit] = useState(null); // {lineId, value}
  // Phase 0: workflow fields, primary owner, notes & activity timeline
  const { me, locLabel } = useDealerContext();
  const myRole = me?.dealer_role || (me?.location_scope === "location" ? "LOCATION_MANAGER" : "DEALER_ADMIN");
  const canReassign = myRole === "DEALER_ADMIN" || myRole === "LOCATION_MANAGER";
  const [activity, setActivity] = useState([]);
  const [team, setTeam] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [noteSource, setNoteSource] = useState("TYPED");
  const [noteSaving, setNoteSaving] = useState(false);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [ownerTarget, setOwnerTarget] = useState("");
  const [nextStepEdit, setNextStepEdit] = useState(null); // null = not editing
  const [followUpEdit, setFollowUpEdit] = useState(null);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostNote, setLostNote] = useState("");
  const [generating, setGenerating] = useState(false);
  // RC48-PROP-002…005: structured proposal blockers; RC48-PROJ-014: proactive readiness
  const [proposalBlockers, setProposalBlockers] = useState(null);
  const [readiness, setReadiness] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      setNextStepEdit(null);
      setFollowUpEdit(null);
      const act = await api.get(`/projects/${id}/activity`);
      setActivity(act.data);
      api.get(`/projects/${id}/proposal-readiness`).then((res) => setReadiness(res.data)).catch(() => {});
    } catch (e) {
      setNavFail(reasonFromError(e));  // NAV-009: visible, recoverable — never a silent redirect
    }
  };

  useEffect(() => {
    load();
    api.get("/catalog").then((res) => setCatalog(res.data)).catch(() => {});
    api.get("/dealer/users").then((res) => setTeam(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patchWorkflow = async (patch, successMsg) => {
    try {
      await api.patch(`/projects/${id}/workflow`, patch);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleOwnerSave = async () => {
    try {
      await api.post(`/projects/${id}/owner`, { user_id: ownerTarget === "UNASSIGNED" ? null : ownerTarget });
      toast.success("Primary owner updated");
      setOwnerOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    setNoteSaving(true);
    try {
      await api.post(`/projects/${id}/notes`, { body: noteBody.trim(), source: noteSource });
      toast.success("Note added");
      setNoteBody("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setNoteSaving(false);
    }
  };

  const handleAddLine = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${id}/lines`, {
        variant_id: lineForm.variant_id,
        area: lineForm.area,
        ...(lineForm.area_sqft ? { area_sqft: parseFloat(lineForm.area_sqft) } : {}),
        ...(lineForm.waste_pct !== "" ? { waste_pct: parseInt(lineForm.waste_pct, 10) } : {}),
      });
      toast.success("Line added — quantities calculated from variant box coverage");
      setAddOpen(false);
      setLineForm({ variant_id: "", waste_pct: "", area: "", area_sqft: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleReserveLine = async (line) => {
    try {
      const { data } = await api.post(`/projects/${id}/lines/${line.id}/reserve`, {});
      if (data.status === "PENDING_APPROVAL") {
        toast.info("Repeat hold — sent to Zelligio for approval. Stock is not held yet.");
      } else {
        toast.success(`Reserved ${data.boxes} boxes for ${data.hold_hours}h`);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDeleteLine = async (line) => {
    try {
      await api.delete(`/projects/${id}/lines/${line.id}`);
      toast.success("Line removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLineRetailRevert = async (line) => {
    try {
      await api.delete(`/projects/${id}/lines/${line.id}/retail-price`);
      toast.success("Using your Catalog standing price again");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleLineRetail = async (line) => {
    try {
      const body = line.box_coverage_sqft_snapshot ? { retail_price_per_sqft: parseFloat(retailEdit.value) } : { retail_price: parseFloat(retailEdit.value) };
      try {
        await api.put(`/projects/${id}/lines/${line.id}/retail-price`, body);
      } catch (e1) {
        const d = e1.response?.data?.detail;
        if (e1.response?.status === 409 && d?.code === "below_cost") {
          if (!window.confirm(`${d.message}\n\nWholesale cost: $${d.wholesale_cost_per_sqft}/sq ft · Your price: $${d.proposed_per_sqft}/sq ft · Margin: ${d.margin_pct}%\n\nKeep this price anyway?`)) throw e1;
          await api.put(`/projects/${id}/lines/${line.id}/retail-price`, { ...body, acknowledge_below_cost: true });
        } else throw e1;
      }
      toast.success("Project selling price saved — your Catalog standing price is unchanged");
      setRetailEdit(null);
      // keep the proposal panel open until every missing customer price is set, then Continue stays available
      setProposalBlockers((bl) => (bl ? bl.filter((b) => b.line_id !== line.id) : bl));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // RC48-PROJ-011/015: per-line sourcing request carries the project context (SRC-007)
  const handleLineSourcing = async (line) => {
    try {
      await api.post("/dealer/sourcing-requests", { variant_id: line.variant_id, boxes: line.required_boxes, project_id: id });
      toast.success("Sourcing requested — Zelligio evaluates Florida, incoming and origin supply");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleQuoteStatus = async (status) => {    try {
      await api.post(`/projects/${id}/quote-status`, { status });
      toast.success(`Quote marked ${status.toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // §27.5: LOST_CLOSED requires a structured reason — captured before the stage commits
  const handleStageChange = (v) => {
    if (v === "LOST_CLOSED") {
      setLostReason(project.lost_reason || "");
      setLostNote(project.lost_note || "");
      setLostOpen(true);
      return;
    }
    patchWorkflow({ stage: v });
  };

  const handleLostSave = async () => {
    if (!lostReason) return;
    await patchWorkflow({ stage: "LOST_CLOSED", lost_reason: lostReason, ...(lostNote.trim() ? { lost_note: lostNote.trim() } : {}) }, "Project marked lost");
    setLostOpen(false);
  };

  // QUO-02: client-safe proposal from this project's priced lines
  const handleGenerateProposal = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/dealer/projects/${id}/proposals`);
      toast.success(`Proposal ${data.proposal_number} created`);
      navigate("/dealer/proposals");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // RC48-PROP-002/003/004: actionable blocker — exact lines + Set Customer Price
      if (err.response?.status === 422 && detail?.code === "MISSING_CUSTOMER_PRICE") {
        setProposalBlockers((detail.lines || []).map((l) => ({ issue: "MISSING_CUSTOMER_PRICE", ...l })));
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleWasteChange = async (line, waste) => {
    try {
      await api.patch(`/projects/${id}/lines/${line.id}`, { waste_pct: Math.max(0, Math.min(100, parseInt(waste, 10) || 0)) });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (navFail) return <NavigationFailure reason={navFail} moduleLabel="Projects" modulePath="/dealer/projects" />;
  if (!project) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="project-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const isNewProject = ["sample_status", "quote_status", "reservation_status", "order_status"].every((f) => project[f] === "NONE");

  return (
    <div data-testid="project-detail-page">
      <Button variant="ghost" onClick={() => navigate("/dealer/projects")} data-testid="back-to-projects" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> All projects
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">{project.display_stage !== "Planning" ? project.display_stage : "Project"}</p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">{project.name}</h1>
          <p className="mt-2 text-sm text-warmgrey">
            {project.projected_sqft} sqft · {project.waste_pct}% overage
            {project.client_name ? ` · ${project.client_name}` : ""}
            {project.designer_name ? ` · ${project.designer_name}` : ""}
          </p>
          <p className="mt-1 text-xs text-warmgrey" data-testid="project-meta-line">
            {locLabel(project.location_id) ? `${locLabel(project.location_id)} · ` : ""}
            Owner: {project.owner_name || "Unassigned"}
            {project.last_activity_at ? ` · Last activity ${new Date(project.last_activity_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.dispatchEvent(new CustomEvent("copilot:open", { detail: { kind: "project", id: project.id, label: project.name } }))}
          data-testid="ask-copilot-project-button"
          className="self-start border-limestone text-charcoal hover:bg-sand"
        >
          Ask ZELI
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="milestone-chips">
        {Object.entries(MILESTONE_LABEL).map(([field, label]) => {
          const active = project[field] !== "NONE";
          const recommend = isNewProject && field === "sample_status";
          return (
            <span key={field} className={`badge-status ${active ? "badge-available" : recommend ? "border border-terracotta bg-terracotta text-white" : "badge-neutral"}`} data-testid={`milestone-${label.toLowerCase()}`}>
              {label}{active ? ` · ${project[field].replace(/_/g, " ")}` : recommend ? " · recommended start" : ""}
            </span>
          );
        })}
      </div>

      {project.quote_status !== "NONE" && project.quote_status !== "ACCEPTED" && (
        <div className="mt-6 flex gap-3">
          {project.quote_status === "DRAFT" && (
            <Button variant="outline" size="sm" onClick={() => handleQuoteStatus("PRESENTED")} data-testid="quote-presented-button" className="border-limestone text-charcoal hover:bg-sand">
              Mark quote presented
            </Button>
          )}
          {project.quote_status === "PRESENTED" && (
            <Button variant="outline" size="sm" onClick={() => handleQuoteStatus("ACCEPTED")} data-testid="quote-accepted-button" className="border-limestone text-charcoal hover:bg-sand">
              Mark quote accepted
            </Button>
          )}
        </div>
      )}

      {project.lines.length > 0 && (
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={handleGenerateProposal} disabled={generating}
            data-testid="generate-proposal-button" className="border-terracotta text-terracotta hover:bg-[#BC6955]/5">
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            Generate client proposal
          </Button>
        </div>
      )}

      {/* RC48-PROJ-014 + PROP-002…005: actionable blockers before/at proposal generation —
          correction preserves context; Continue retries without restarting (PROP-005) */}
      {(proposalBlockers || (readiness?.blockers || []).length > 0) && (() => {
        const blockers = proposalBlockers || readiness.blockers;
        const priceBlockers = blockers.filter((b) => b.issue === "MISSING_CUSTOMER_PRICE");
        const noLines = blockers.some((b) => b.issue === "NO_LINES");
        const allSet = blockers.length === 0;
        return (
        <div className={`mt-4 rounded-md border p-4 ${allSet ? "border-border bg-sand/50" : "border-status-sold/40 bg-[#8C3A3A]/5"}`} data-testid="proposal-blockers">
          {allSet && <p className="text-sm text-charcoal" data-testid="blocker-all-set">All customer prices set — continue to generate the proposal.</p>}
          {/* RC51-QA-002: the message names the actual blocker — never "price missing" when there are no lines */}
          {noLines && priceBlockers.length === 0 && (
            <p className="text-sm text-charcoal" data-testid="blocker-no-lines">Proposal not available yet — add at least one product line to this project first.</p>
          )}
          {priceBlockers.length > 0 && (
            <>
              <p className="text-sm font-semibold text-status-sold">Proposal blocked — a customer price is missing on {priceBlockers.length} line{priceBlockers.length > 1 ? "s" : ""}:</p>
              <p className="mt-1 text-xs text-warmgrey" data-testid="blocker-resale-explainer">Resale prices are optional for wholesale ordering, samples and reservations — they are only needed for the price your client sees. Enter the resale price for each line below, then continue.</p>
              <ul className="mt-2 space-y-2">
                {priceBlockers.map((b) => (
                  <li key={b.line_id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-charcoal" data-testid={`blocker-${b.line_id}`}>
                    <span>{dealerLabel(b)}</span>
                    <Button size="sm" onClick={() => { setRetailEdit({ lineId: b.line_id, value: "" }); setTimeout(() => { const el = document.querySelector(`[data-testid="line-retail-input-${b.line_id}"]`); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus(); }, 50); }} data-testid={`blocker-set-price-${b.line_id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">Set customer price</Button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {proposalBlockers && (
            <Button size="sm" variant="outline" className="mt-3 border-limestone text-charcoal hover:bg-sand"
              onClick={() => { setProposalBlockers(null); handleGenerateProposal(); }} data-testid="proposal-continue">
              Continue proposal
            </Button>
          )}
        </div>
        );
      })()}
      {(readiness?.warnings || []).length > 0 && (
        <div className="mt-4 rounded-md border border-[#D4A373]/40 bg-[#D4A373]/10 p-4" data-testid="proposal-warnings">
          <p className="text-sm font-semibold text-[#8a6435]">Availability needs attention:</p>
          <ul className="mt-2 space-y-1 text-sm text-charcoal">
            {readiness.warnings.map((w) => (
              <li key={w.line_id} data-testid={`warning-${w.line_id}`}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}

      {project.stage === "LOST_CLOSED" && project.lost_reason && (
        <p className="mt-4 text-sm text-status-sold" data-testid="project-lost-reason">
          Lost — {PROJECT_LOST_REASONS[project.lost_reason] || project.lost_reason}{project.lost_note ? `: ${project.lost_note}` : ""}
        </p>
      )}

      <Card className="mt-8 border-border bg-white" data-testid="project-workflow-card">
        <CardContent className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Stage</Label>
            <Select value={project.stage} onValueChange={handleStageChange}>
              <SelectTrigger data-testid="project-stage-select" className="h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {Object.entries(PROJECT_STAGE).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`stage-option-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Primary owner</Label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex h-10 items-center text-sm text-charcoal" data-testid="project-owner-name">{project.owner_name || "Unassigned"}</span>
              {canReassign && (
                <Button variant="outline" size="sm" onClick={() => { setOwnerTarget(project.primary_owner_user_id || "UNASSIGNED"); setOwnerOpen(true); }} data-testid="reassign-owner-button" className="border-limestone text-charcoal hover:bg-sand">
                  <UserRound className="mr-1 h-3.5 w-3.5" /> Reassign
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Next step</Label>
            {nextStepEdit === null ? (
              <button onClick={() => setNextStepEdit(project.next_step || "")} data-testid="next-step-display" className="flex h-10 items-center text-left text-sm text-charcoal hover:text-terracotta">
                {project.next_step || "Set next step"}
              </button>
            ) : (
              <div className="flex gap-2">
                <Input value={nextStepEdit} onChange={(e) => setNextStepEdit(e.target.value)} data-testid="next-step-input" className="h-10 bg-white focus-visible:ring-terracotta" placeholder="e.g. Confirm contractor measurements" />
                <Button size="sm" onClick={() => patchWorkflow({ next_step: nextStepEdit }, "Next step saved")} data-testid="next-step-save" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">Save</Button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Follow-up date</Label>
            <DatePickerField
              value={followUpEdit ?? project.follow_up_date ?? ""}
              onChange={(v) => { setFollowUpEdit(v); if (v !== (project.follow_up_date || "")) patchWorkflow({ follow_up_date: v || null }, v ? "Follow-up date set" : "Follow-up date cleared"); }}
              clearable={false} className="h-10" testId="follow-up-date-input"
            />
            {project.follow_up_date && (
              <button onClick={() => patchWorkflow({ follow_up_date: null }, "Follow-up date cleared")} data-testid="follow-up-clear" className="text-xs text-warmgrey hover:text-charcoal">Clear</button>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Decision target</Label>
            <DatePickerField value={project.target_decision_date ?? ""}
              onChange={(v) => patchWorkflow({ target_decision_date: v || null })} className="h-10" testId="target-decision-date" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Delivery target</Label>
            <DatePickerField value={project.target_delivery_date ?? ""}
              onChange={(v) => patchWorkflow({ target_delivery_date: v || null })} className="h-10" testId="target-delivery-date" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Install target</Label>
            <DatePickerField value={project.target_installation_date ?? ""}
              onChange={(v) => patchWorkflow({ target_installation_date: v || null })} className="h-10" testId="target-installation-date" />
          </div>
        </CardContent>
      </Card>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-warmgrey">
          Quantities use each product's box coverage and your overage — whole boxes only.
        </p>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setSampleOpen(true)} data-testid="project-sample-button" className="h-10 border-terracotta text-terracotta hover:bg-[#BC6955]/5">
            <FlaskConical className="mr-2 h-4 w-4" /> Request sample
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-line-button" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> Add product line
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border bg-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">Add product line</DialogTitle>
              <DialogDescription className="text-sm text-warmgrey">Lines represent areas/applications — the same variant can be added multiple times.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddLine} className="mt-2 space-y-5" data-testid="add-line-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Variant *</Label>
                <Select value={lineForm.variant_id} onValueChange={(v) => setLineForm((f) => ({ ...f, variant_id: v }))}>
                  <SelectTrigger data-testid="line-variant-select" className="h-11 bg-white"><SelectValue placeholder="Select variant" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {catalog.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.label} · {v.box_coverage_sqft} sqft/box</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Area / room</Label>
                  <Input data-testid="line-area-input" value={lineForm.area} onChange={(e) => setLineForm((f) => ({ ...f, area: e.target.value }))} placeholder="e.g. Master Bath" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Area sqft</Label>
                  <Input type="number" step="0.1" min="1" data-testid="line-sqft-input" value={lineForm.area_sqft} onChange={(e) => setLineForm((f) => ({ ...f, area_sqft: e.target.value }))} placeholder={`Project total (${project.projected_sqft})`} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Overage %</Label>
                  <Input type="number" min="0" max="100" step="1" data-testid="line-waste-select" value={lineForm.waste_pct} onChange={(e) => setLineForm((f) => ({ ...f, waste_pct: e.target.value }))} placeholder={`Project default (${project.waste_pct}%)`} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">Recommended overage: 15% — any value allowed, including 0%. Leave blank to use the project default.</p>
              <Button type="submit" disabled={saving || !lineForm.variant_id} data-testid="add-line-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add line"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {project.lines.length === 0 ? (
        <div className="mt-6 rounded-md border border-dashed border-limestone bg-white p-10 text-center" data-testid="lines-empty">
          <p className="text-sm font-medium text-charcoal">Choose products for this project</p>
          <p className="mt-1 text-sm text-warmgrey">Add a product to calculate boxes and material needed.</p>
        </div>
      ) : (
        <>
          {/* mobile: readable stacked cards instead of a wide table */}
          <div className="mt-6 space-y-3 md:hidden">
            {project.lines.map((l) => (
              <div key={l.id} className="rounded-md border border-border bg-white p-4" data-testid={`line-card-${l.id}`}>
                <p className="font-medium text-charcoal">{dealerLabel(l)}</p>
                {l.area && <p className="mt-0.5 text-xs text-warmgrey">{l.area}{l.area_sqft ? ` · ${l.area_sqft} sqft` : ""}</p>}
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Material needed (incl. {l.waste_pct}% overage)</dt><dd className="text-charcoal num">{l.required_sqft} sqft</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Recommended whole boxes</dt><dd className="font-semibold text-charcoal num" data-testid={`line-boxes-${l.id}`}>{l.required_boxes}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Actual coverage</dt><dd className="text-charcoal num">{l.actual_coverage_sqft ?? "—"} sqft</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Florida available</dt><dd className="text-charcoal num" data-testid={`line-florida-${l.id}`}>{l.florida_available_boxes ?? 0} boxes</dd></div>
                  {l.incoming_boxes > 0 && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Incoming</dt><dd className="text-charcoal num" data-testid={`line-incoming-${l.id}`}>{l.incoming_boxes} boxes</dd></div>}
                  {l.margin_pct != null && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Margin</dt><dd className="text-charcoal num" data-testid={`line-margin-${l.id}`}>{l.margin_pct}%</dd></div>}
                  {l.est_material_subtotal != null && (
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Est. material subtotal</dt><dd className="text-charcoal num">${l.est_material_subtotal.toFixed(2)}</dd></div>
                  )}
                </dl>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => handleReserveLine(l)} data-testid={`reserve-line-${l.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">Reserve {l.required_boxes}</Button>
                  {l.needs_sourcing && (
                    <Button size="sm" variant="outline" onClick={() => handleLineSourcing(l)} data-testid={`line-sourcing-${l.id}`} className="border-terracotta text-terracotta hover:bg-[#BC6955]/5">Request sourcing</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteLine(l)} data-testid={`delete-line-${l.id}`} className="text-status-sold"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 hidden overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)] md:block">
        <Table data-testid="project-lines-table" className="min-w-[900px]">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Variant</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Area</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Overage</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Material needed</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Whole boxes</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Actual coverage</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Availability</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Your price/box</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Est. material</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {project.lines.map((l) => (
                <TableRow key={l.id} className="border-border" data-testid={`line-row-${l.id}`}>
                  <TableCell className="py-4">
                    <p className="font-medium text-charcoal">{dealerLabel(l)}</p>
                    <p className="font-mono text-xs text-muted-foreground">{l.box_coverage_sqft_snapshot} sqft/box</p>
                  </TableCell>
                  <TableCell className="py-4 text-sm text-warmgrey">
                    {l.area || "—"}
                    {l.area_sqft ? <span className="block text-xs text-muted-foreground num">{l.area_sqft} sqft</span> : null}
                  </TableCell>
                  <TableCell className="py-4">
                    <Input
                      type="number" min="0" max="100" step="1"
                      defaultValue={l.waste_pct}
                      key={`${l.id}-${l.waste_pct}`}
                      onBlur={(e) => parseInt(e.target.value, 10) !== l.waste_pct && handleWasteChange(l, e.target.value)}
                      className="h-9 w-20 bg-white"
                      data-testid={`line-waste-${l.id}`}
                    />
                  </TableCell>
                  <TableCell className="py-4 text-right text-sm text-charcoal" data-testid={`line-sqft-${l.id}`}>{l.required_sqft}</TableCell>
                  <TableCell className="py-4 text-right text-sm font-semibold text-charcoal" data-testid={`line-boxes-${l.id}`}>{l.required_boxes}</TableCell>
                  <TableCell className="py-4 text-right text-sm text-charcoal num" data-testid={`line-coverage-${l.id}`}>{l.actual_coverage_sqft ?? "—"}</TableCell>
                  <TableCell className="py-4 text-right text-sm" data-testid={`line-availability-${l.id}`}>
                    {l.needs_sourcing ? (
                      <span className="badge-status badge-committed" data-testid={`line-needs-sourcing-${l.id}`}>Needs sourcing</span>
                    ) : (
                      <span className="text-charcoal num">{l.florida_available_boxes ?? 0} in FL</span>
                    )}
                    {l.incoming_boxes > 0 && (
                      <p className="text-xs text-warmgrey num">{l.incoming_boxes} incoming{l.pre_reservable_boxes > 0 ? ` · ${l.pre_reservable_boxes} pre-reservable` : ""}</p>
                    )}
                  </TableCell>
                  <TableCell className="py-4 text-right">
                    {retailEdit?.lineId === l.id ? (
                      <form onSubmit={(e) => { e.preventDefault(); handleLineRetail(l); }} className="flex flex-col items-end gap-1">
                        <div className="flex items-center justify-end gap-2">
                          <Input type="number" step="0.01" min="0" value={retailEdit.value} onChange={(e) => setRetailEdit({ lineId: l.id, value: e.target.value })} className="h-9 w-24 bg-white focus-visible:ring-terracotta" data-testid={`line-retail-input-${l.id}`} aria-label="Project selling price per square foot" />
                          <span className="text-xs text-muted-foreground">{l.box_coverage_sqft_snapshot ? "/sq ft" : "/box"}</span>
                          <Button size="sm" type="submit" data-testid={`line-retail-save-${l.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">Save</Button>
                        </div>
                        {l.box_coverage_sqft_snapshot ? <p className="text-[11px] text-muted-foreground" data-testid={`line-retail-box-preview-${l.id}`}>= ${retailEdit.value && !isNaN(parseFloat(retailEdit.value)) ? (parseFloat(retailEdit.value) * l.box_coverage_sqft_snapshot).toFixed(2) : "0.00"}/box · project override only</p> : null}
                      </form>
                    ) : (
                      <button
                        onClick={() => setRetailEdit({ lineId: l.id, value: (l.box_coverage_sqft_snapshot ? l.retail_price_per_sqft : l.retail_price_snapshot) ?? "" })}
                        className="text-sm text-charcoal hover:text-terracotta"
                        data-testid={`line-retail-edit-${l.id}`}
                        title="Project selling price — overrides your Catalog standing price for this project only"
                      >
                        {l.retail_price_snapshot != null ? (
                          <span className="num">{l.retail_price_per_sqft != null ? `$${l.retail_price_per_sqft.toFixed(2)}/sq ft · ` : ""}${l.retail_price_snapshot.toFixed(2)}/box</span>
                        ) : "Set price per square foot"}
                      </button>
                    )}
                    {l.retail_price_snapshot != null && retailEdit?.lineId !== l.id && (
                      <p className="mt-0.5 text-[10px] uppercase tracking-wide text-warmgrey" data-testid={`line-retail-source-${l.id}`}>
                        {l.retail_price_source === "override" ? (
                          <>Project override · <button type="button" className="normal-case text-terracotta hover:underline" onClick={() => handleLineRetailRevert(l)} data-testid={`line-retail-revert-${l.id}`}>use Catalog price</button></>
                        ) : l.retail_price_source === "format_default" ? "Format default price" : "Catalog standing price"}
                      </p>
                    )}
                    {l.margin_pct != null && <p className="mt-0.5 text-[11px] text-warmgrey num" data-testid={`line-margin-${l.id}`}>margin {l.margin_pct}%</p>}
                  </TableCell>
                  <TableCell className="py-4 text-right text-sm text-charcoal num" data-testid={`line-est-material-${l.id}`}>
                    {l.est_material_subtotal != null ? `$${l.est_material_subtotal.toFixed(2)}` : "—"}
                  </TableCell>
                  <TableCell className="py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {l.needs_sourcing && (
                        <Button size="sm" variant="outline" onClick={() => handleLineSourcing(l)} data-testid={`line-sourcing-${l.id}`} className="border-terracotta text-terracotta hover:bg-[#BC6955]/5">
                          Request sourcing
                        </Button>
                      )}
                      <Button size="sm" onClick={() => handleReserveLine(l)} data-testid={`reserve-line-${l.id}`} className="bg-terracotta text-white hover:bg-terracotta-hover">
                        Reserve {l.required_boxes}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteLine(l)} data-testid={`delete-line-${l.id}`} className="text-status-sold">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
          </div>
        </>
      )}

      {project.reservations?.length > 0 && (
        <>
          <p className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Linked reservations</p>
          <div className="mt-4 space-y-3">
            {project.reservations.map((r) => (
              <Card key={r.id} className="border-border bg-white" data-testid={`project-reservation-${r.id}`}>
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <p className="text-sm text-charcoal">{dealerLabel(r)} · {r.boxes} boxes · {r.source === "FLORIDA" ? "Florida" : "In transit"}</p>
                  <span className="badge-status badge-neutral">{r.effective_status.replace(/_/g, " ")}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-border bg-white" data-testid="project-notes-card">
          <CardContent className="p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Project notes</p>
            <form onSubmit={handleAddNote} className="mt-4 space-y-3" data-testid="note-form">
              <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} data-testid="note-body-input" placeholder="Add an internal note — stays with the project, attributed to you" className="min-h-20 bg-white focus-visible:ring-terracotta" />
              <div className="flex items-center justify-between gap-3">
                <Select value={noteSource} onValueChange={setNoteSource}>
                  <SelectTrigger data-testid="note-source-select" className="h-9 w-36 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="TYPED" data-testid="note-source-typed">Typed</SelectItem>
                    {/* RC48-NOTE-001/002: voice capture is not implemented — the option is
                        disabled and plainly labelled instead of looking functional (NOTE-003:
                        project-note voice stays distinct from Copilot voice) */}
                    <SelectItem value="VOICE" disabled data-testid="note-source-voice">Voice — Coming Later</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" disabled={noteSaving || !noteBody.trim()} data-testid="note-submit" className="bg-terracotta text-white hover:bg-terracotta-hover">
                  {noteSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}
                </Button>
              </div>
            </form>
            <div className="mt-5 space-y-3">
              {activity.filter((a) => a.kind === "note").length === 0 ? (
                <p className="text-sm text-warmgrey" data-testid="notes-empty">No notes yet.</p>
              ) : (
                activity.filter((a) => a.kind === "note").map((n) => (
                  <div key={n.id} className="rounded-md border border-limestone bg-sand p-3" data-testid={`note-${n.id}`}>
                    <p className="whitespace-pre-wrap text-sm text-charcoal">{n.body}</p>
                    <p className="mt-2 flex items-center gap-2 text-xs text-warmgrey">
                      {n.source === "VOICE" && <Mic className="h-3 w-3" />}
                      {n.author_name} · {new Date(n.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-white" data-testid="project-activity-card">
          <CardContent className="p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Activity</p>
            <div className="mt-4 space-y-3">
              {activity.length === 0 ? (
                <p className="text-sm text-warmgrey" data-testid="activity-empty">No activity yet.</p>
              ) : (
                activity.map((a) => (
                  <div key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-terracotta" />
                    <div>
                      <p className="text-sm text-charcoal">
                        {a.kind === "note" ? (
                          <><MessageSquareText className="mr-1 inline h-3.5 w-3.5 text-warmgrey" />{a.author_name} added a note{a.source === "VOICE" ? " (voice)" : ""}</>
                        ) : (
                          EVENT_TEXT(a)
                        )}
                      </p>
                      <p className="text-xs text-warmgrey">{new Date(a.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={ownerOpen} onOpenChange={setOwnerOpen}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="reassign-owner-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Reassign primary owner</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Responsibility only — the project always belongs to the dealership. You can also leave it unassigned (e.g. after a departure).
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <Select value={ownerTarget} onValueChange={setOwnerTarget}>
              <SelectTrigger data-testid="owner-target-select" className="h-11 bg-white"><SelectValue placeholder="Select teammate" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="UNASSIGNED" data-testid="owner-option-unassigned">Unassigned — keep at this location</SelectItem>
                {team.filter((u) => u.active).map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`owner-option-${u.id}`}>
                    {u.name}{u.dealer_role === "DEALER_ADMIN" ? " · Dealer Admin" : u.location_id === project.location_id ? "" : " · other location"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleOwnerSave} disabled={!ownerTarget} data-testid="owner-save" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">Save owner</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Mark project lost</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              A structured reason is required — it keeps pipeline reporting honest. The project and its history are never deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="lost-reason-select" className="h-11 bg-white"><SelectValue placeholder="Select loss reason *" /></SelectTrigger>
              <SelectContent className="bg-white">
                {Object.entries(PROJECT_LOST_REASONS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`lost-reason-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea value={lostNote} onChange={(e) => setLostNote(e.target.value)} placeholder="Note (optional)"
              data-testid="lost-note-input" className="min-h-[70px] bg-white focus-visible:ring-terracotta" />
            <Button onClick={handleLostSave} disabled={!lostReason} data-testid="lost-confirm"
              className="h-11 w-full bg-status-sold text-white hover:opacity-90">Mark lost / closed</Button>
          </div>
        </DialogContent>
      </Dialog>

      <SampleRequestDialog
        open={sampleOpen}
        onOpenChange={setSampleOpen}
        catalog={catalog}
        projects={project ? [project] : []}
        preselectedProjectId={id}
        context="project"
        onSubmitted={load}
      />
    </div>
  );
}
